import React, { useState, useRef, useEffect } from 'react';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';
import GivingBadge from '../../shared/components/GivingBadge';
import ActionsMenu from '../../shared/components/ActionsMenu';
import GivingConfirmModal from '../../shared/components/GivingConfirmModal';
import GivingRecurringCancelOptions from '../../shared/components/GivingRecurringCancelOptions';
import { formatCurrency, campaignTypeLabel, formatDate, escapeHtmlAttr, daysLeftDisplay, campaignStatusDisplay } from '../../shared/givingHelpers';
import PreviewModal from './PreviewModal';
import EmbedModal from './EmbedModal';
import AppealSection from './AppealSection';
import '../../GivingCampaignForm/styles/givingCampaignForm.css';
import '../styles/givingCampaignDetail.css';
import { copyText } from '@opendate-app/setlist';

const GivingCampaignDetail = ({
  giving_campaign,
  team,
}) => {
  const campaign = giving_campaign;
  const goalCents = campaign.goal_amount_cents || 0;
  const raisedCents = campaign.total_raised_cents || 0;
  const hasGoal = goalCents > 0;
  const hasRaised = raisedCents > 0;
  const progressPct = Math.min(campaign.progress_percentage || 0, 100);

  const [transitioning, setTransitioning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [recurringHandling, setRecurringHandling] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareMenuRef = useRef(null);
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShareMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const node = topBarRef.current;
    if (!node) return undefined;
    setTopBarHeight(node.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setTopBarHeight(entry.contentRect.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const baseUrl = window.location.origin;
  const embedCode = campaign.widget_path ? `<script src="${baseUrl}/packs/od_giving_embed.js"></script>
<iframe src="${baseUrl}${campaign.widget_path}"
  id="od-giving-${campaign.id}-iframe" title="${escapeHtmlAttr(campaign.name)}"
  scrolling="no" allowpaymentrequest="true" style="border:none; width: 1px; min-width: 100%;" height="600px;"></iframe>
<script>
  document.addEventListener('DOMContentLoaded', function() {
    ODGivingEmbed("od-giving-${campaign.id}-iframe");
  });
</script>` : '';

  const copyToClipboard = (text) => copyText(text);

  const flashCopied = (key) => {
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCopyUrl = () => {
    copyToClipboard(`${baseUrl}${campaign.public_path}`);
    flashCopied('url');
    setShareMenuOpen(false);
  };

  const handleCopyCode = () => {
    if (!campaign.campaign_code) return;
    copyToClipboard(campaign.campaign_code);
    flashCopied('code');
  };

  const handleCopyEmbed = () => {
    copyToClipboard(embedCode);
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  const handleOpenEmbedModal = () => {
    setEmbedCopied(false);
    setEmbedModalOpen(true);
    setShareMenuOpen(false);
  };

  const recurringCount = campaign.active_recurring_donation_count || 0;

  const requestStatusTransition = (action) => {
    if (transitioning) return;
    setRecurringHandling(null);
    setPendingAction(action);
  };

  const closeConfirmModal = () => {
    setPendingAction(null);
    setRecurringHandling(null);
  };

  const confirmStatusTransition = async () => {
    if (transitioning || !pendingAction) return;
    const action = pendingAction;
    if (action === 'delete' && recurringCount > 0 && !recurringHandling) return;
    setTransitioning(true);
    try {
      if (action === 'delete') {
        const data = recurringCount > 0 ? { recurring_donation_handling: recurringHandling } : undefined;
        await client.delete(`/teams/${team.id}/giving/${campaign.id}.json`, { data });
        window.location.href = `/teams/${team.id}/giving`;
        return;
      }
      await client.patch(`/teams/${team.id}/giving/${campaign.id}/${action}.json`);
      window.location.reload();
    } catch (err) {
      const errors = err.response?.data?.errors;
      toastError(errors ? errors.join(', ') : 'Could not update status');
      setTransitioning(false);
      closeConfirmModal();
    }
  };

  const STATUS_CONFIRM_COPY = {
    activate: {
      title: 'Publish this campaign?',
      body: 'Once published, this campaign becomes publicly visible at its donation URL and donors can begin contributing right away. You can mark it complete later, but you can\'t un-publish.',
      confirmLabel: 'Publish campaign',
      pendingLabel: 'Publishing...',
    },
    complete: {
      title: 'Mark this campaign as complete?',
      body: 'Completing this campaign stops new contributions. The public page will still be visible, but the donate button will be disabled.',
      confirmLabel: 'Mark complete',
      pendingLabel: 'Completing...',
    },
    delete: {
      title: 'Delete this campaign?',
      body: 'This removes the campaign from your dashboard. Past donations are kept, but the campaign and its public page will no longer be accessible.',
      confirmLabel: 'Delete campaign',
      pendingLabel: 'Deleting...',
    },
  };
  const pendingCopy = pendingAction ? STATUS_CONFIRM_COPY[pendingAction] : null;

  const status = campaignStatusDisplay(campaign.display_status);
  const formatLabel = (campaign.format || 'page').charAt(0).toUpperCase() + (campaign.format || 'page').slice(1);

  const isLive = campaign.status === 'active' || campaign.status === 'completed';
  const previewSrc = isLive && campaign.widget_path ? campaign.widget_path : null;

  const hasShareActions = campaign.public_path || campaign.widget_path;

  const daysLeft = daysLeftDisplay(campaign);

  return (
    <div
      className="gc-builder gc-builder--readonly"
      style={topBarHeight ? { '--gcd-top-h': `${topBarHeight}px` } : undefined}
    >
      <div className="gc-top-bar gc-top-bar--sticky" ref={topBarRef}>
        <a href={`/teams/${team.id}/giving`} className="gc-back-link">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" />
          </svg>
          Back to Campaigns
        </a>
        <div className="gc-page-header">
          <div className="gc-detail-titlewrap">
            <h2 className="gc-page-title">{campaign.name}</h2>
            <div className="gc-detail-meta">
              <GivingBadge variant={status.variant} dot>{status.label}</GivingBadge>
              <GivingBadge variant="modern">{formatLabel}</GivingBadge>
              {campaign.campaign_type && (
                <GivingBadge variant="modern">{campaignTypeLabel(campaign.campaign_type)}</GivingBadge>
              )}
              {campaign.campaign_code && (
                <button
                  type="button"
                  className="gcd-code-chip"
                  onClick={handleCopyCode}
                  title="Click to copy"
                  aria-label={`Copy campaign code ${campaign.campaign_code}`}
                >
                  <span className="gcd-code-chip-label">CODE</span>
                  <span className="gcd-code-chip-value">{campaign.campaign_code}</span>
                  {copiedField === 'code' ? (
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                      <path d="M3 8l3 3 7-7" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                      <rect x="5" y="5" width="9" height="9" rx="1.5" />
                      <path d="M3 11H2.5a1 1 0 01-1-1V2.5a1 1 0 011-1h7.5a1 1 0 011 1V3" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="gc-header-actions">
            {previewSrc && (
              <button
                type="button"
                className="btn btn-setlist-secondary gcd-preview-toggle"
                onClick={() => setPreviewModalOpen(true)}
              >
                <svg style={{ width: 16, height: 16 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Preview
              </button>
            )}
            {hasShareActions && (
              <div className="gcd-share-menu" ref={shareMenuRef}>
                <button
                  type="button"
                  className="btn btn-setlist-secondary gcd-share-trigger"
                  onClick={() => setShareMenuOpen(!shareMenuOpen)}
                  aria-haspopup="menu"
                  aria-expanded={shareMenuOpen}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share
                  <svg viewBox="0 0 20 20" fill="none" width="14" height="14" style={{ marginLeft: 4 }}>
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {shareMenuOpen && (
                  <div className="gcd-share-menu-list" role="menu">
                    {campaign.public_path && (
                      <button type="button" className="gcd-share-menu-item" role="menuitem" onClick={handleCopyUrl}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                        <span>{copiedField === 'url' ? 'Link copied!' : 'Copy link'}</span>
                      </button>
                    )}
                    {campaign.public_path && (
                      <a
                        className="gcd-share-menu-item"
                        role="menuitem"
                        href={campaign.public_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShareMenuOpen(false)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        <span>View public page</span>
                      </a>
                    )}
                    {campaign.widget_path && (
                      <button type="button" className="gcd-share-menu-item" role="menuitem" onClick={handleOpenEmbedModal}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="16 18 22 12 16 6" />
                          <polyline points="8 6 2 12 8 18" />
                        </svg>
                        <span>Embed code</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <a href={`/teams/${team.id}/giving/${campaign.id}/edit`} className="btn btn-setlist-secondary">
              Edit
            </a>
            {campaign.status === 'planned' && (
              <button
                className="btn btn-setlist-primary"
                onClick={() => requestStatusTransition('activate')}
                disabled={transitioning}
              >
                {transitioning ? 'Publishing...' : 'Publish'}
              </button>
            )}
            {campaign.status === 'active' && (
              <button
                className="btn btn-setlist-primary"
                onClick={() => requestStatusTransition('complete')}
                disabled={transitioning}
              >
                {transitioning ? 'Completing...' : 'Complete Campaign'}
              </button>
            )}
            <ActionsMenu
              buttonClassNames={['team-actions-btn-square']}
              actions={[
                {
                  label: 'Delete campaign',
                  icon: 'fal fa-trash-alt',
                  danger: true,
                  onClick: () => requestStatusTransition('delete'),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="gc-builder-content">
        <div className="gc-builder-main">

          <a
            className="gc-section"
            href={`/teams/${team.id}/reporting/donations/giving-by-campaign/gifts?drilldown_id=${campaign.id}`}
            aria-label="View the full donations report for this campaign"
            style={{ display: 'block', color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
          >
            <div className="gc-section-row">
              <div className="gc-section-info">
                <h3 className="gc-section-title">Progress</h3>
                <p className="gc-section-desc">Total raised against the campaign goal. View the full donations report &rarr;</p>
              </div>
              <div className="gc-section-fields">
                <div className="gcd-progress-row">
                  <span className="gcd-progress-raised">{formatCurrency(raisedCents)} raised</span>
                  {hasGoal && (
                    <span className="gcd-progress-goal">
                      <span className="gcd-progress-pct">{progressPct}% of goal</span>
                      <span className="gcd-progress-divider" aria-hidden="true">&middot;</span>
                      {formatCurrency(goalCents)}
                    </span>
                  )}
                </div>
                {hasGoal ? (
                  <div className="gcd-progress-bar">
                    {hasRaised && (
                      <div className="gcd-progress-fill" style={{ width: `${progressPct}%` }} />
                    )}
                    {!hasRaised && (
                      <span className="gcd-progress-empty-label">0%</span>
                    )}
                  </div>
                ) : null}
                {campaign.milestones && campaign.milestones.length > 0 && (
                  <div className="gcd-milestones">
                    {campaign.milestones.map((m) => (
                      <span key={m.id}>{m.label} ({m.target_percentage}%)</span>
                    ))}
                  </div>
                )}
                <div className="gcd-stats-strip">
                  <div className="gcd-stat">
                    <span className="gcd-stat-value">{campaign.donor_count}</span>
                    <span className="gcd-stat-label">Donors</span>
                  </div>
                  <div className="gcd-stat">
                    <span className="gcd-stat-value">{campaign.gift_count}</span>
                    <span className="gcd-stat-label">Gifts</span>
                  </div>
                  <div className="gcd-stat">
                    {daysLeft?.kind === 'active' && (
                      <>
                        <span className="gcd-stat-value">{daysLeft.value}</span>
                        <span className="gcd-stat-label">Days Left</span>
                      </>
                    )}
                    {daysLeft?.kind === 'past_due' && (
                      <>
                        <span className="gcd-stat-value gcd-stat-value--badge">
                          <GivingBadge variant="warning">Ended</GivingBadge>
                        </span>
                        <span className="gcd-stat-label">{daysLeft.endLabel}</span>
                      </>
                    )}
                    {!daysLeft && (
                      <>
                        <span className="gcd-stat-value gcd-stat-value--muted">—</span>
                        <span className="gcd-stat-label">Days Left</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </a>

          <div className="gc-section">
            <div className="gc-section-row">
              <div className="gc-section-info">
                <h3 className="gc-section-title">Campaign Details</h3>
                <p className="gc-section-desc">Core information about this campaign.</p>
              </div>
              <div className="gc-section-fields">
                <div className="gc-field-row">
                  <div className="gc-field">
                    <label className="gc-label">Campaign Name</label>
                    <div className="gc-value">{campaign.name}</div>
                  </div>
                  <div className="gc-field">
                    <label className="gc-label">Campaign Code</label>
                    <div className="gc-value">{campaign.campaign_code || <span className="gc-value-empty">Not set</span>}</div>
                  </div>
                </div>

                {campaign.format !== 'form' && (
                  <div className="gc-field-row">
                    <div className="gc-field">
                      <label className="gc-label">Campaign Type</label>
                      <div className="gc-value">
                        {campaign.campaign_type
                          ? campaignTypeLabel(campaign.campaign_type)
                          : <span className="gc-value-empty">Not set</span>}
                      </div>
                    </div>
                    <div className="gc-field">
                      <label className="gc-label">Goal Amount</label>
                      <div className="gc-value">{formatCurrency(goalCents)}</div>
                    </div>
                  </div>
                )}

                {campaign.format === 'form' && hasGoal && (
                  <div className="gc-field">
                    <label className="gc-label">Goal</label>
                    <div className="gc-value">{formatCurrency(goalCents)}</div>
                  </div>
                )}

                {campaign.format !== 'form' && (
                  <div className="gc-field-row">
                    <div className="gc-field">
                      <label className="gc-label">Start Date</label>
                      <div className="gc-value">{formatDate(campaign.start_date)}</div>
                    </div>
                    <div className="gc-field">
                      <label className="gc-label">End Date</label>
                      <div className="gc-value">
                        {formatDate(campaign.end_date)}
                        {campaign.past_end_date && (
                          <span className="gcd-past-due-wrap">
                            <GivingBadge variant="warning">Ended</GivingBadge>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="gc-field">
                  <label className="gc-label">Venue</label>
                  <div className="gc-value">
                    {campaign.venue_ownership
                      ? campaign.venue_ownership.name
                      : <span className="gc-value-empty">Not set</span>}
                  </div>
                </div>

                {campaign.linked_events?.length > 0 && (
                  <div className="gc-field">
                    <label className="gc-label">{campaign.linked_events.length === 1 ? 'Linked Event' : 'Linked Events'}</label>
                    {campaign.linked_events.map((event) => (
                      <div key={event.id} className="gc-value">
                        <a href={event.path}>{event.title}</a>
                        {event.date && ` · ${formatDate(event.date)}`}
                      </div>
                    ))}
                  </div>
                )}

                {campaign.fund && (
                  <div className="gc-field">
                    <label className="gc-label">Default Fund</label>
                    <div className="gc-value">{campaign.fund.name}</div>
                  </div>
                )}

                <div className="gc-field">
                  <label className="gc-label">Description</label>
                  {campaign.description ? (
                    <div className="gcd-prose">{campaign.description}</div>
                  ) : (
                    <div className="gcd-prose gcd-prose--empty">
                      <span className="gc-value-empty">No description provided.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(campaign.headline || campaign.organizer_name || campaign.cover_image_url || campaign.primary_color || campaign.button_text) && (
            <div className="gc-section">
              <div className="gc-section-row">
                <div className="gc-section-info">
                  <h3 className="gc-section-title">{campaign.format === 'form' ? 'Form Branding' : 'Public Page Settings'}</h3>
                  <p className="gc-section-desc">How the campaign appears to donors.</p>
                </div>
                <div className="gc-section-fields">
                  {campaign.format !== 'form' && campaign.headline && (
                    <div className="gc-field">
                      <label className="gc-label">Headline</label>
                      <div className="gc-value">{campaign.headline}</div>
                    </div>
                  )}

                  {campaign.primary_color && (
                    <div className="gc-field">
                      <label className="gc-label">Theme Color</label>
                      <div className="gc-color-row">
                        <span className="gcd-color-swatch" style={{ background: campaign.primary_color }} />
                        <span className="gc-value gc-color-text">{campaign.primary_color}</span>
                      </div>
                    </div>
                  )}

                  {campaign.button_text && (
                    <div className="gc-field">
                      <label className="gc-label">Button Text</label>
                      <div className="gc-value">{campaign.button_text}</div>
                    </div>
                  )}

                  {campaign.organizer_name && (
                    <div className="gc-field">
                      <label className="gc-label">Organizer Name</label>
                      <div className="gc-value">{campaign.organizer_name}</div>
                    </div>
                  )}

                  {campaign.cover_image_url && (
                    <div className="gc-field">
                      <label className="gc-label">Cover Image</label>
                      <img className="gcd-cover-image" src={campaign.cover_image_url} alt="Cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <AppealSection campaign={campaign} team={team} />

        </div>

        <div className="gc-builder-sidebar">
          <div className="gc-preview-panel">
            <div className="gc-preview-header">
              <span className="gc-preview-label">Preview</span>
              <GivingBadge variant={isLive ? 'success' : 'gray'} dot>
                {isLive ? 'Live' : 'Not Live'}
              </GivingBadge>
            </div>
            {previewSrc ? (
              <iframe
                className="gcd-sidebar-iframe"
                src={previewSrc}
                title={`Preview: ${campaign.name}`}
              />
            ) : (
              <div className="gcd-preview-empty">
                <div className="gcd-preview-empty-icon" aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 2l20 20" />
                    <path d="M6.71 6.71C3.4 8.27 1 12 1 12s4 8 11 8a10.94 10.94 0 005.29-1.29" />
                    <path d="M9.88 5.09A10.94 10.94 0 0112 5c7 0 11 8 11 8a13.16 13.16 0 01-1.67 2.68" />
                    <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
                  </svg>
                </div>
                <p className="gcd-preview-empty-title">This campaign isn't live yet</p>
                <p className="gcd-preview-empty-hint">Once you publish, the public page will appear here.</p>
                {campaign.status === 'planned' && (
                  <button
                    type="button"
                    className="btn btn-setlist-secondary"
                    onClick={() => requestStatusTransition('activate')}
                    disabled={transitioning}
                  >
                    {transitioning ? 'Publishing...' : 'Publish campaign'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {previewModalOpen && (
        <PreviewModal campaign={campaign} onClose={() => setPreviewModalOpen(false)} />
      )}

      {embedModalOpen && (
        <EmbedModal embedCode={embedCode} onCopy={handleCopyEmbed} copied={embedCopied} onClose={() => setEmbedModalOpen(false)} />
      )}

      <GivingConfirmModal
        open={!!pendingCopy}
        title={pendingCopy?.title}
        body={pendingCopy?.body}
        confirmLabel={pendingCopy?.confirmLabel}
        pendingLabel={pendingCopy?.pendingLabel}
        pending={transitioning}
        danger={pendingAction === 'delete'}
        confirmDisabled={pendingAction === 'delete' && recurringCount > 0 && !recurringHandling}
        onConfirm={confirmStatusTransition}
        onCancel={closeConfirmModal}
      >
        {pendingAction === 'delete' && (
          <GivingRecurringCancelOptions
            count={recurringCount}
            amountCents={campaign.active_recurring_donation_amount_cents}
            value={recurringHandling}
            onChange={setRecurringHandling}
          />
        )}
      </GivingConfirmModal>
    </div>
  );
};

export default GivingCampaignDetail;
